/**
 * Partner Preview / Test Hub.
 *
 * Route: /partner/test-hub
 *
 * A single read-only landing page that surfaces every PWS-partner
 * facing feature in one place — including the three features that
 * previously had backend endpoints only (Success Pack, Sample
 * Collection settings, Wholesale Bundles).
 *
 * PINNED SAFETY RULE (Feb 2026):
 *   This page is preview / visibility ONLY. It makes the same GET
 *   calls the rest of the partner portal makes — it never POSTs,
 *   PUTs, archives, mutates, or dispatches anything.
 *   Specifically: no emails, no SMS, no notifications, no invites,
 *   no password resets, no ServiceM8 jobs, no work orders, no
 *   quotes/proposals, no Stripe calls, no invoices, no wholesale
 *   orders, no fulfilment records, no sample-collection dispatch,
 *   no route planning, no customer-facing changes. Partners stay
 *   read-only for Success Pack, Sample Collection, Wholesale
 *   Bundles and bookings/reports.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ResultsCallScriptCard } from "@/pages/partner/ResultsCallScriptCard";
import {
  ShieldCheck, ExternalLink, Copy, CheckCircle2, XCircle, Loader2,
  LayoutDashboard, ListChecks, FileText, ShieldCheck as ShieldIcon,
  Sparkles, Truck, Package, Globe, Activity, AlertTriangle,
  Phone, Mail, Briefcase, Megaphone, Users, Info,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PartnerShell } from "./Dashboard";


// ── Endpoints exercised by the API-health card.
const PARTNER_ENDPOINTS = [
  { key: "dashboard",         path: "/partner/dashboard",
    label: "Dashboard",         icon: LayoutDashboard },
  { key: "reports",           path: "/partner/reports",
    label: "Reports",           icon: FileText },
  { key: "success_pack",      path: "/partner/pws-success-pack",
    label: "Success Pack",      icon: Sparkles },
  { key: "sample_collection", path: "/partner/pws-sample-collection",
    label: "Sample Collection", icon: Truck },
  { key: "wholesale_bundles", path: "/partner/pws-wholesale-bundles",
    label: "Wholesale Bundles", icon: Package },
];


const SCRIPT_ICONS = {
  phone:             Phone,
  email:             Mail,
  property_manager:  Briefcase,
  social_post:       Megaphone,
  in_person:         Users,
};

const SCRIPT_LABEL = {
  phone:             "Phone script",
  email:             "Email script",
  property_manager:  "Property-manager script",
  social_post:       "Social post",
  in_person:         "In-person script",
};


function copy(text, label = "Copied") {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(
    () => toast.success(label, { duration: 4000 }),
    () => toast.error("Copy failed"),
  );
}


function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  return `$${Number(v).toFixed(2)}`;
}


export default function PartnerTestHub() {
  const [dash, setDash] = useState(null);
  const [pack, setPack] = useState(null);
  const [sample, setSample] = useState(null);
  const [bundles, setBundles] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(true);

  const runHealth = useCallback(async () => {
    const next = {};
    await Promise.all(PARTNER_ENDPOINTS.map(async (ep) => {
      const t0 = Date.now();
      try {
        const r = await api.get(ep.path);
        next[ep.key] = {
          ok: true, status: r.status, ms: Date.now() - t0,
        };
      } catch (err) {
        next[ep.key] = {
          ok: false,
          status: err?.response?.status ?? 0,
          ms: Date.now() - t0,
          detail: err?.response?.data?.detail || err.message,
        };
      }
    }));
    setHealth(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, sp, sc, wb, bk] = await Promise.allSettled([
          api.get("/partner/dashboard"),
          api.get("/partner/pws-success-pack"),
          api.get("/partner/pws-sample-collection"),
          api.get("/partner/pws-wholesale-bundles"),
          api.get("/partner/bookings"),
        ]);
        if (cancelled) return;
        if (d.status === "fulfilled") setDash(d.value.data);
        if (sp.status === "fulfilled") setPack(sp.value.data);
        if (sc.status === "fulfilled") setSample(sc.value.data);
        if (wb.status === "fulfilled") setBundles(wb.value.data);
        if (bk.status === "fulfilled") setBookings(bk.value.data?.bookings || bk.value.data?.rows || []);
      } finally {
        if (!cancelled) {
          setLoading(false);
          runHealth();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [runHealth]);

  const partner = dash?.partner || {};
  const partnerSlug = partner.partner_slug
                      || pack?.partner?.partner_slug
                      || pack?.partner_slug
                      || (pack?.sections || []).find((s) => s.slug === "your_partner_booking_link")?.partner_slug
                      || "PWS-PHASE5-DEMO";
  const sampleBookingRef = useMemo(() => {
    const first = bookings[0];
    return first?.reference || first?.booking_reference || null;
  }, [bookings]);
  const publicBookingHref = `/book?p=${encodeURIComponent(partnerSlug)}`;

  return (
    <PartnerShell active="aware tools & links">
      <div
        data-testid="partner-test-hub"
        className="space-y-6"
      >
        <header>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">
            Partner Tools
          </div>
          <h1
            data-testid="partner-test-hub-heading"
            className="text-3xl sm:text-4xl font-bold text-slate-900"
          >
            AWARE Tools &amp; Links
          </h1>
          <p
            data-testid="partner-test-hub-subtext"
            className="mt-2 text-base text-slate-600 max-w-3xl"
          >
            Your AWARE referral tools, landing page, QR code and booking
            links — all in one place.
          </p>
          <p className="mt-2 text-sm text-slate-600 max-w-3xl">
            Every PWS Partner feature available to this account, using the
            same GET endpoints the rest of the portal calls — nothing on
            this page can be edited and nothing is dispatched.
          </p>
        </header>

        <Link
          to="/partner/marketing-resources"
          data-testid="partner-test-hub-crosslink-marketing"
          className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors"
        >
          <span className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">
              Looking for banners, graphics or promotional material?
            </span>{" "}
            Go to Marketing &amp; Referrals.
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 whitespace-nowrap">
            <Megaphone className="w-4 h-4" /> Open
          </span>
        </Link>

        <div
          data-testid="partner-test-hub-safety-banner"
          className="flex items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-5 py-4 text-sm text-emerald-900"
        >
          <ShieldCheck className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
          <div>
            <div className="font-semibold">Preview / visibility only.</div>
            <div className="mt-0.5">
              No emails, SMS, notifications, invites, password resets,
              ServiceM8 jobs, work orders, quotes, Stripe calls,
              invoices, wholesale orders, fulfilment records, sample
              collection dispatch, route planning or customer-facing
              changes are triggered from this page.
            </div>
          </div>
        </div>

        {loading ? (
          <div
            data-testid="partner-test-hub-loading"
            className="rounded-xl bg-white border border-slate-200 p-8 text-center text-slate-500"
          >
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
            Loading partner features…
          </div>
        ) : null}

        {!loading ? (
          <>
            <QuickLinks sampleBookingRef={sampleBookingRef} />
            <PublicBookingCard slug={partnerSlug} href={publicBookingHref} />
            <SuccessPackCard pack={pack} />
            <ResultsCallScriptCard />
            <SampleCollectionCard sample={sample} />
            <WholesaleBundlesCard bundles={bundles} />
            <HealthCard health={health} onRefresh={runHealth} />
          </>
        ) : null}
      </div>
    </PartnerShell>
  );
}


// ── 1-4. Quick links to existing partner pages ─────────────────────────
function QuickLinks({ sampleBookingRef }) {
  const items = [
    { to: "/partner/dashboard", label: "Partner Dashboard",
      icon: LayoutDashboard, hint: "KPIs, attributions, lead value snapshot",
      testid: "test-hub-link-dashboard" },
    { to: "/partner/bookings", label: "Partner Bookings",
      icon: ListChecks, hint: "Attributed bookings only",
      testid: "test-hub-link-bookings" },
    { to: "/partner/passports", label: "Partner Passports",
      icon: ShieldIcon, hint: "AWARE Water Health Records linked to your bookings",
      testid: "test-hub-link-passports" },
    { to: "/partner/reports", label: "Partner Reports",
      icon: FileText, hint: "White-labelled customer report PDFs",
      testid: "test-hub-link-reports" },
  ];
  return (
    <section
      data-testid="test-hub-quick-links"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <h2 className="font-display text-lg font-bold text-slate-900 mb-3">
        Existing partner pages
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className="rounded-xl border border-slate-200 p-4 hover:border-cyan-300 hover:bg-cyan-50 transition-colors block"
            >
              <Icon className="w-5 h-5 text-cyan-700" />
              <div className="mt-2 font-semibold text-sm text-slate-900">
                {it.label}
              </div>
              <div className="text-xs text-slate-500 mt-1">{it.hint}</div>
            </Link>
          );
        })}
      </div>
      {sampleBookingRef ? (
        <div className="mt-4 text-xs text-slate-600">
          Example booking detail link:{" "}
          <Link
            data-testid="test-hub-sample-booking-link"
            to={`/partner/bookings/${sampleBookingRef}`}
            className="font-mono text-cyan-700 hover:text-cyan-900 underline"
          >
            /partner/bookings/{sampleBookingRef}
          </Link>
        </div>
      ) : (
        <div className="mt-4 text-xs text-slate-400">
          No attributed bookings yet — once one lands you&apos;ll see a
          direct test link here.
        </div>
      )}
    </section>
  );
}


// ── 7. Public booking link card ────────────────────────────────────────
function PublicBookingCard({ slug, href }) {
  return (
    <section
      data-testid="test-hub-public-booking-card"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-start gap-3">
        <Globe className="w-5 h-5 text-cyan-600 mt-1" />
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Your public booking link
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            This is the URL your customers should land on. It silently
            records partner attribution for 90 days.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code
              data-testid="test-hub-public-booking-url"
              className="text-xs font-mono bg-slate-100 text-slate-800 px-3 py-1.5 rounded-md"
            >
              {href}
            </code>
            <button
              type="button"
              data-testid="test-hub-copy-public-booking-url"
              onClick={() => copy(`${window.location.origin}${href}`,
                                   "Public booking URL copied")}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-cyan-700 border border-slate-200 rounded-full px-3 py-1.5"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
            <a
              data-testid="test-hub-open-public-booking"
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-cyan-600 hover:bg-cyan-700 rounded-full px-3 py-1.5"
            >
              Open in new tab <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            Partner slug:{" "}
            <span
              data-testid="test-hub-partner-slug"
              className="font-mono text-slate-700"
            >
              {slug}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}


// ── 4. Success Pack ────────────────────────────────────────────────────
function SuccessPackCard({ pack }) {
  if (!pack) {
    return (
      <section
        data-testid="test-hub-success-pack-card"
        className="rounded-2xl bg-white border border-slate-200 p-5"
      >
        <div className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">
            Success Pack is not available for this account.
          </span>
        </div>
      </section>
    );
  }
  const sections = pack.sections || [];
  const findSection = (slug) => sections.find((s) => s.slug === slug);

  const bookingUrl = pack.booking_url
                     || findSection("your_partner_booking_link")?.booking_url;
  const qrPng = pack.qr_endpoints?.png_data_url;
  const howToExplain = findSection("how_to_explain");
  const scripts = howToExplain?.scripts || [];           // array of {channel, body}
  const disclosure = findSection("approved_disclosure_wording");
  const disclosureLines = Array.isArray(disclosure?.body)
                          ? disclosure.body
                          : (disclosure?.body ? [disclosure.body] : []);
  const whatNot = findSection("what_not_to_say");
  const helpful = findSection("helpful_links");
  const helpfulLinks = helpful?.links || [];

  return (
    <section
      data-testid="test-hub-success-pack-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 space-y-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-fuchsia-600 mt-1" />
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">
              Success Pack
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Read-only · scripts, QR code, disclosure wording and
              what-not-to-say guidance.
            </p>
          </div>
        </div>
      </div>

      {bookingUrl ? (
        <div data-testid="test-hub-sp-booking-url-block">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
            Your partner booking link
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <code className="text-xs font-mono bg-slate-100 text-slate-800 px-3 py-1.5 rounded-md break-all">
              {bookingUrl}
            </code>
            <button
              onClick={() => copy(bookingUrl, "Booking link copied")}
              data-testid="test-hub-sp-copy-booking-url"
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-cyan-700 border border-slate-200 rounded-full px-3 py-1.5"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>
      ) : null}

      {qrPng ? (
        <div data-testid="test-hub-sp-qr-block">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            QR code
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <img
              data-testid="test-hub-sp-qr-image"
              src={qrPng}
              alt="Partner QR code"
              className="w-32 h-32 border border-slate-200 rounded-lg bg-white"
            />
            <a
              data-testid="test-hub-sp-download-qr"
              href={qrPng}
              download="partner-qr.png"
              className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
            >
              <ExternalLink className="w-3 h-3" /> Download PNG
            </a>
          </div>
        </div>
      ) : null}

      {disclosure && disclosureLines.length > 0 ? (
        <div data-testid="test-hub-sp-disclosure-block">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
            Approved disclosure wording
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
            {disclosureLines.map((line, i) => (
              <p key={i} className="text-sm text-slate-700 italic leading-relaxed">
                {line}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      {scripts.length > 0 ? (
        <div data-testid="test-hub-sp-scripts-block" className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
            Approved scripts
          </div>
          <ul className="space-y-2">
            {scripts.map((script, idx) => {
              const channel = script.channel || `script-${idx}`;
              const Icon = SCRIPT_ICONS[channel] || Info;
              const text = script.body || "";
              return (
                <li
                  key={channel}
                  data-testid={`test-hub-sp-script-${channel}`}
                  className="border border-slate-200 rounded-md p-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-xs font-semibold text-slate-700">
                      {SCRIPT_LABEL[channel] || channel}
                    </span>
                    <button
                      onClick={() => copy(text, "Script copied")}
                      data-testid={`test-hub-sp-script-copy-${channel}`}
                      className="ml-auto text-[10px] uppercase tracking-wider font-bold text-slate-400 hover:text-cyan-700"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {text}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {whatNot && (whatNot.items || []).length > 0 ? (
        <div data-testid="test-hub-sp-what-not-to-say-block">
          <div className="text-[11px] uppercase tracking-wider font-bold text-rose-700 mb-1">
            What not to say
          </div>
          {whatNot.intro ? (
            <p className="text-xs text-slate-600 mb-2">{whatNot.intro}</p>
          ) : null}
          <ul className="space-y-1.5">
            {(whatNot.items || []).map((it, i) => (
              <li
                key={i}
                data-testid={`test-hub-sp-do-not-say-${i}`}
                className="text-xs"
              >
                <span className="font-semibold text-rose-700">
                  Avoid:
                </span>{" "}
                <span className="text-slate-700">
                  &ldquo;{it.forbidden_claim}&rdquo;
                </span>
                <div className="text-slate-500 ml-4">
                  Why: {it.reason}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {helpfulLinks.length > 0 ? (
        <div data-testid="test-hub-sp-helpful-links-block">
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
            Helpful links
          </div>
          <ul className="text-xs text-cyan-700 space-y-1">
            {helpfulLinks.map((l, i) => (
              <li key={i}>
                {l.href ? (
                  <a
                    data-testid={`test-hub-sp-link-${i}`}
                    href={l.href}
                    target={l.external ? "_blank" : undefined}
                    rel="noreferrer"
                    className="hover:underline"
                  >
                    {l.label || l.href}
                  </a>
                ) : (
                  <span>{l.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}


// ── 5. Sample Collection ───────────────────────────────────────────────
function SampleCollectionCard({ sample }) {
  if (!sample) {
    return (
      <section
        data-testid="test-hub-sample-collection-card"
        className="rounded-2xl bg-white border border-slate-200 p-5"
      >
        <div className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">
            Sample collection settings are not available for this
            account.
          </span>
        </div>
      </section>
    );
  }
  const s = sample.settings || {};
  return (
    <section
      data-testid="test-hub-sample-collection-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3"
    >
      <div className="flex items-start gap-3">
        <Truck className="w-5 h-5 text-lime-700 mt-1" />
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Sample collection settings
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Read-only view. Aware admin configures these for you —
            partners cannot edit collection settings from the portal.
          </p>
        </div>
        <span
          data-testid="test-hub-sc-enabled-badge"
          className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${
            s.local_sample_collection_enabled
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : "bg-slate-50 text-slate-700 border-slate-200"
          }`}
        >
          {s.local_sample_collection_enabled ? "Enabled" : "Not enabled"}
        </span>
      </div>

      <dl
        data-testid="test-hub-sc-detail"
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm"
      >
        <div>
          <dt className="text-[11px] uppercase font-bold text-slate-500">
            Service area
          </dt>
          <dd
            data-testid="test-hub-sc-service-area"
            className="text-slate-800"
          >
            {s.local_sample_collection_service_area || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase font-bold text-slate-500">
            Postcodes
          </dt>
          <dd
            data-testid="test-hub-sc-postcodes"
            className="text-slate-800"
          >
            {(s.local_sample_collection_postcodes || []).join(", ")
              || "all postcodes"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase font-bold text-slate-500">
            Price mode
          </dt>
          <dd
            data-testid="test-hub-sc-price-mode"
            className="text-slate-800"
          >
            {s.local_sample_collection_price_mode || "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase font-bold text-slate-500">
            Contact method
          </dt>
          <dd
            data-testid="test-hub-sc-contact-method"
            className="text-slate-800"
          >
            {s.local_sample_collection_contact_method || "—"}
          </dd>
        </div>
        {s.local_sample_collection_note ? (
          <div className="sm:col-span-2">
            <dt className="text-[11px] uppercase font-bold text-slate-500">
              Partner note
            </dt>
            <dd
              data-testid="test-hub-sc-note"
              className="text-slate-700 italic"
            >
              {s.local_sample_collection_note}
            </dd>
          </div>
        ) : null}
      </dl>

      <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2">
        Partners cannot edit these settings · No dispatch · No
        scheduling · No fee applied · {sample.safety_banner || "Visibility only."}
      </div>
    </section>
  );
}


// ── 6. Wholesale Bundles ───────────────────────────────────────────────
function WholesaleBundlesCard({ bundles }) {
  if (!bundles) {
    return (
      <section
        data-testid="test-hub-wholesale-bundles-card"
        className="rounded-2xl bg-white border border-slate-200 p-5"
      >
        <div className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">
            Wholesale bundle catalogue is not available for this
            account.
          </span>
        </div>
      </section>
    );
  }
  const rows = bundles.rows || [];
  return (
    <section
      data-testid="test-hub-wholesale-bundles-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3"
    >
      <div className="flex items-start gap-3">
        <Package className="w-5 h-5 text-violet-700 mt-1" />
        <div className="flex-1">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Wholesale bundle catalogue
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Read-only · visibility only · this is not an order, not a
            quote, and does not trigger fulfilment.
          </p>
        </div>
      </div>

      <div
        data-testid="test-hub-wb-safety-banner"
        className="text-[11px] text-slate-600 border border-slate-200 bg-slate-50 rounded-md px-3 py-2"
      >
        {bundles.safety_banner ||
          "Internal partner catalogue · visibility only · this is not an order, not a quote, and no fulfilment is triggered from this page. Availability and pricing to be confirmed by Aware."}
      </div>

      {rows.length === 0 ? (
        <div
          data-testid="test-hub-wb-empty"
          className="text-sm text-slate-500 italic"
        >
          No visible bundles for your partner account yet.
        </div>
      ) : (
        <ul
          data-testid="test-hub-wb-list"
          className="divide-y divide-slate-100"
        >
          {rows.map((b) => (
            <li
              key={b.id}
              data-testid={`test-hub-wb-row-${b.id}`}
              className="py-3"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900">
                    {b.bundle_name}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wider">
                    {b.bundle_type}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">
                    {b.price_status === "hidden"
                      ? "Price hidden"
                      : (b.price_status || "estimate")}
                  </div>
                  <div className="text-sm font-bold text-slate-800">
                    {b.price_status === "hidden"
                      ? "—"
                      : fmtMoney(b.wholesale_price)}
                  </div>
                </div>
              </div>
              {b.description ? (
                <p className="text-xs text-slate-600 mt-1">
                  {b.description}
                </p>
              ) : null}
              {b.included_items && b.included_items.length > 0 ? (
                <ul className="mt-1 text-xs text-slate-700 list-disc list-inside">
                  {b.included_items.map((it, i) => (
                    <li
                      key={i}
                      data-testid={`test-hub-wb-item-${b.id}-${i}`}
                    >
                      {it.label}
                      {it.quantity !== null && it.quantity !== undefined
                        ? ` × ${it.quantity}` : ""}
                      {it.note ? ` — ${it.note}` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
              {b.fulfilment_note ? (
                <div
                  data-testid={`test-hub-wb-fulfilment-${b.id}`}
                  className="mt-1 text-[11px] text-slate-500 italic"
                >
                  {b.fulfilment_note}
                </div>
              ) : null}
              <div className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                Not an order · Not a quote · No fulfilment triggered
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


// ── 8. API health card ────────────────────────────────────────────────
function HealthCard({ health, onRefresh }) {
  return (
    <section
      data-testid="test-hub-api-health-card"
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <Activity className="w-5 h-5 text-slate-700 mt-1" />
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">
              API health · partner endpoints
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Live GET status — each endpoint returns 200 for this
              partner.
            </p>
          </div>
        </div>
        <button
          type="button"
          data-testid="test-hub-health-refresh"
          onClick={onRefresh}
          className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 border border-cyan-200 rounded-full px-3 py-1.5"
        >
          Refresh
        </button>
      </div>
      <ul
        data-testid="test-hub-health-list"
        className="divide-y divide-slate-100"
      >
        {PARTNER_ENDPOINTS.map((ep) => {
          const h = health[ep.key];
          const Icon = ep.icon;
          const ok = h?.ok;
          const StatusIcon = ok ? CheckCircle2 : (h ? XCircle : Loader2);
          return (
            <li
              key={ep.key}
              data-testid={`test-hub-health-row-${ep.key}`}
              className="py-2.5 flex items-center gap-3 text-sm"
            >
              <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <code className="font-mono text-xs text-slate-700 flex-1 truncate">
                GET {ep.path}
              </code>
              <span
                data-testid={`test-hub-health-status-${ep.key}`}
                className={`inline-flex items-center gap-1 text-xs font-bold ${
                  ok ? "text-emerald-700"
                  : h ? "text-rose-700" : "text-slate-400"
                }`}
              >
                <StatusIcon className={`w-4 h-4 ${!h ? "animate-spin" : ""}`} />
                {h ? `${h.status} · ${h.ms}ms` : "checking…"}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
